/**
 * Offline Indicator Component
 * Shows offline status and sync queue information
 */

'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { WifiOff, Wifi, RefreshCw, AlertCircle } from 'lucide-react';
import { isOnline, getSyncQueueStatus, processSyncQueue } from '@/lib/services/sync';
import { useSession } from '@/lib/auth/client';

export function OfflineIndicator() {
  const [online, setOnline] = useState(true);
  const [syncStatus, setSyncStatus] = useState<{
    pending: number;
    failed: number;
    total: number;
  } | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const { data: session } = useSession();
  const router = useRouter();

  useEffect(() => {
    // Set initial online status
    setOnline(isOnline());

    // Listen for online/offline events
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Update sync status periodically when online
    const updateSyncStatus = async () => {
      if (session?.user?.id && online) {
        try {
          const status = await getSyncQueueStatus(session.user.id);
          setSyncStatus(status);
        } catch (error) {
          console.error('Failed to get sync status:', error);
        }
      }
    };

    // Initial update
    updateSyncStatus();

    // Update every 30 seconds
    const interval = setInterval(updateSyncStatus, 30000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, [online, session?.user?.id]);

  const handleSync = async () => {
    if (!session?.user?.id || !online || isSyncing) return;

    setIsSyncing(true);
    try {
      await processSyncQueue(session.user.id);
      // Refresh the page to show updated data
      router.refresh();
      // Update sync status
      const status = await getSyncQueueStatus(session.user.id);
      setSyncStatus(status);
    } catch (error) {
      console.error('Sync failed:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  // Don't show if online and no pending sync items
  if (online && (!syncStatus || syncStatus.total === 0)) {
    return null;
  }

  return (
    <div
      className={`fixed bottom-4 right-4 z-50 rounded-lg border p-4 shadow-lg transition-all ${
        online
          ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950'
          : 'border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950'
      }`}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-3">
        {online ? (
          <Wifi className="h-5 w-5 text-green-600 dark:text-green-400" />
        ) : (
          <WifiOff className="h-5 w-5 text-orange-600 dark:text-orange-400" />
        )}

        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {online ? 'Online' : 'Offline'}
          </p>
          {syncStatus && syncStatus.total > 0 && (
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {syncStatus.pending > 0 && (
                <span>{syncStatus.pending} pending {syncStatus.pending === 1 ? 'item' : 'items'}</span>
              )}
              {syncStatus.failed > 0 && (
                <span className="ml-2 text-orange-600 dark:text-orange-400">
                  {syncStatus.failed} failed {syncStatus.failed === 1 ? 'item' : 'items'}
                </span>
              )}
            </p>
          )}
        </div>

        {online && syncStatus && syncStatus.pending > 0 && (
          <button
            onClick={handleSync}
            disabled={isSyncing}
            className="flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            aria-label="Sync pending items"
          >
            {isSyncing ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span>Syncing...</span>
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                <span>Sync</span>
              </>
            )}
          </button>
        )}

        {syncStatus && syncStatus.failed > 0 && (
          <AlertCircle
            className="h-5 w-5 text-orange-600 dark:text-orange-400"
            aria-label="Some items failed to sync"
          />
        )}
      </div>
    </div>
  );
}




